import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { backendService } from "../../../services/backend.service";
import type { PbjPlanProgress, PbjTrackedPlan, PbjTrackedPlanSaveBody } from "../../../utils/models";
import { PBJ_MODEL_QUERY_KEY } from "./usePbjModel";

const TRACKED_PLAN_KEY = "tracked-plan" as const;

const progressQueryKey = (departmentId: string) => [PBJ_MODEL_QUERY_KEY, TRACKED_PLAN_KEY, "progress", departmentId] as const;

/**
 * Progress against the department's tracked plan.
 *
 * Deliberately NOT keyed on the page's committed date range: the plan carries its own quarter and
 * the endpoint always measures over that, so re-scoping the Prediction tab must not refetch — or
 * worse, cache — a different answer for the same plan.
 *
 * `null` data is a real, successful state (the department tracks nothing), which is why the
 * endpoint answers 200 + null rather than a 404. Callers distinguish it from "still loading" via
 * `isPending`, never by testing the data alone.
 */
export const usePbjTrackedPlanProgress = (departmentId: string, enabled = true) => {
	const { trackError } = useAnalytics();
	return useQuery<PbjPlanProgress | null>({
		queryKey: progressQueryKey(departmentId),
		queryFn: async () => {
			try {
				const res = await backendService.getPbjTrackedPlanProgress(departmentId);
				return res.data;
			} catch (error) {
				trackError("pbj_model_tracked_plan_progress_failed", { error_message: (error as Error)?.message });
				throw error;
			}
		},
		enabled: enabled && !!departmentId,
	});
};

/**
 * Commit the on-screen Action Plan to the tracker.
 *
 * Invalidates the prediction as well as the progress: `prediction.trackedPlan` is what the
 * "Add to tracker" button reads its own state from, so leaving it stale would show "Add to
 * tracker" on a plan that was just saved.
 */
export const useSavePbjTrackedPlan = () => {
	const queryClient = useQueryClient();
	const { showSnackbar } = useSnackbar();
	const { t } = useTranslation();
	const { trackError } = useAnalytics();

	return useMutation<PbjTrackedPlan, Error, PbjTrackedPlanSaveBody>({
		mutationFn: async (body) => {
			const res = await backendService.postPbjTrackedPlan(body);
			return res.data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [PBJ_MODEL_QUERY_KEY, "prediction"] });
			queryClient.invalidateQueries({ queryKey: [PBJ_MODEL_QUERY_KEY, TRACKED_PLAN_KEY] });
			showSnackbar(t("pbj_model.tracker.save_success"), "success");
		},
		onError: (error) => {
			showSnackbar(t("pbj_model.tracker.save_failed"), "error");
			trackError("pbj_model_tracked_plan_save_failed", { error_message: error?.message });
		},
	});
};

/**
 * Stop tracking. Drops the department's whole plan history — idempotent server-side.
 *
 * The progress cache is set to `null` (the endpoint's own "nothing tracked" answer) before the
 * invalidation, so the Tracker drops to its empty state the moment the delete lands rather than
 * showing the deleted plan until the refetch returns. The prediction is invalidated because its
 * `trackedPlan` is what resets the Prediction tab's tracker button to "Add to tracker".
 */
export const useDeletePbjTrackedPlan = () => {
	const queryClient = useQueryClient();
	const { showSnackbar } = useSnackbar();
	const { t } = useTranslation();
	const { track, trackError } = useAnalytics();

	return useMutation<void, Error, string>({
		mutationFn: async (departmentId) => {
			await backendService.deletePbjTrackedPlan(departmentId);
		},
		onMutate: (departmentId) => {
			track("pbj_model_stop_tracking_start", { department_id: departmentId });
		},
		onSuccess: (_, departmentId) => {
			queryClient.setQueryData<PbjPlanProgress | null>(progressQueryKey(departmentId), null);
			queryClient.invalidateQueries({ queryKey: [PBJ_MODEL_QUERY_KEY, "prediction"] });
			queryClient.invalidateQueries({ queryKey: [PBJ_MODEL_QUERY_KEY, TRACKED_PLAN_KEY] });
			showSnackbar(t("pbj_model.tracker.delete_success"), "success");
			track("pbj_model_stop_tracking_success", { department_id: departmentId });
		},
		onError: (error, departmentId) => {
			showSnackbar(t("pbj_model.tracked_plan.delete_failed"), "error");
			trackError("pbj_model_stop_tracking_error", { department_id: departmentId, error_message: error?.message });
		},
	});
};
